import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

const ROLE_OPTIONS = [
  { value: 'chairman', label: 'Chairman' },
  { value: 'vice_chairman', label: 'Vice Chairman' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'member', label: 'Member' },
];

const ROLE_COLORS = {
  chairman: 'bg-purple-100 text-purple-700',
  vice_chairman: 'bg-blue-100 text-blue-700',
  secretary: 'bg-green-100 text-green-700',
  treasurer: 'bg-yellow-100 text-yellow-700',
  member: 'bg-gray-100 text-gray-600',
};

function FormInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <input {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
    </div>
  );
}

export default function AdminCommittee() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', description: '', is_active: true });

  const fetchCommittees = useCallback(() => {
    setLoading(true);
    let url = '/admin/committees/?';
    if (search) url += `search=${encodeURIComponent(search)}&`;
    api.get(url)
      .then(r => setCommittees(r.data.results || r.data))
      .catch(() => toast.error('Failed to load committees'))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { fetchCommittees(); }, [fetchCommittees]);

  const resetForm = () => { setForm({ name: '', description: '', is_active: true }); setEditing(null); setShowForm(false); };

  const handleEdit = (c) => {
    setForm({ name: c.name, description: c.description || '', is_active: c.is_active });
    setEditing(c);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/committees/${editing.id}/`, form);
        toast.success('Committee updated');
      } else {
        await api.post('/admin/committees/', form);
        toast.success('Committee created');
      }
      resetForm();
      fetchCommittees();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to save committee');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this committee and all its members?')) return;
    try {
      await api.delete(`/admin/committees/${id}/`);
      toast.success('Committee deleted');
      if (selectedCommittee?.id === id) setSelectedCommittee(null);
      fetchCommittees();
    } catch { toast.error('Failed to delete committee'); }
  };

  const handleViewMembers = async (committee) => {
    try {
      const r = await api.get(`/admin/committees/${committee.id}/`);
      setSelectedCommittee(r.data);
    } catch { toast.error('Failed to load committee details'); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Committee Management</h1>
      <p className="text-sm text-gray-500 mb-6">Create and manage committees and assign members</p>

      {selectedCommittee ? (
        <CommitteeDetail
          committee={selectedCommittee}
          onBack={() => { setSelectedCommittee(null); fetchCommittees(); }}
          onRefresh={() => handleViewMembers(selectedCommittee)}
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input type="text" placeholder="Search committees..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]" />
            </div>
            {canAdd(PERMISSIONS.ADD_COMMITTEE) && (
              <button onClick={() => { resetForm(); setShowForm(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition whitespace-nowrap">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Committee
              </button>
            )}
          </div>

          {/* Form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">{editing ? 'Edit Committee' : 'New Committee'}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput label="Name *" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                    <span className="text-sm font-medium text-gray-600">Active</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">
                  {editing ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={resetForm} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
              </div>
            </form>
          )}

          {/* List */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Loading...</p>
            </div>
          ) : committees.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-500 mb-2">No Committees</h2>
              <p className="text-gray-400">Create your first committee.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {committees.map((c, idx) => (
                <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition relative">
                  <div className="absolute top-3 left-3 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center">{idx + 1}</div>
                  <div className="ml-8">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-800">{c.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {c.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{c.description}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        <span className="font-semibold text-[#0B5D1E]">{c.member_count}</span> member{c.member_count !== 1 ? 's' : ''}
                      </span>
                      <div className="flex gap-2">
                        <button onClick={() => handleViewMembers(c)} className="text-[#0B5D1E] hover:underline text-xs font-semibold">View</button>
                        {canEdit(PERMISSIONS.EDIT_COMMITTEE) && (
                          <button onClick={() => handleEdit(c)} className="text-blue-600 hover:underline text-xs">Edit</button>
                        )}
                        {canDelete(PERMISSIONS.DELETE_COMMITTEE) && (
                          <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:underline text-xs">Delete</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══ COMMITTEE DETAIL (Members) ═══
function CommitteeDetail({ committee, onBack, onRefresh }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [members, setMembers] = useState(committee.members_list || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [role, setRole] = useState('member');
  const [searching, setSearching] = useState(false);
  const [editingCommittee, setEditingCommittee] = useState(false);
  const [committeeForm, setCommitteeForm] = useState({ name: committee.name, description: committee.description || '', is_active: committee.is_active });
  const [editingMember, setEditingMember] = useState(null);
  const [editRole, setEditRole] = useState('member');

  const fetchMembers = useCallback(() => {
    api.get(`/admin/committees/${committee.id}/members/`)
      .then(r => setMembers(r.data.results || r.data))
      .catch(() => {});
  }, [committee.id]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const searchMembers = useCallback(() => {
    if (memberSearch.length < 2) { setMemberResults([]); return; }
    setSearching(true);
    api.get(`/accounts/admin/members/?search=${encodeURIComponent(memberSearch)}&page_size=10`)
      .then(r => setMemberResults(r.data.results || r.data))
      .catch(() => {})
      .finally(() => setSearching(false));
  }, [memberSearch]);

  useEffect(() => {
    const t = setTimeout(searchMembers, 400);
    return () => clearTimeout(t);
  }, [searchMembers]);

  const handleAddMember = async () => {
    if (!selectedMember) return;
    try {
      await api.post(`/admin/committees/${committee.id}/members/`, {
        member: selectedMember.id,
        role,
      });
      toast.success('Member added to committee');
      setShowAddForm(false);
      setSelectedMember(null);
      setMemberSearch('');
      setRole('member');
      fetchMembers();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.non_field_errors?.[0] || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (cmId) => {
    if (!window.confirm('Remove this member from the committee?')) return;
    try {
      await api.delete(`/admin/committee-members/${cmId}/`);
      toast.success('Member removed');
      fetchMembers();
      onRefresh();
    } catch { toast.error('Failed to remove member'); }
  };

  const handleUpdateRole = async (cmId, newRole) => {
    try {
      await api.patch(`/admin/committee-members/${cmId}/`, { role: newRole });
      toast.success('Role updated');
      fetchMembers();
    } catch { toast.error('Failed to update role'); }
  };

  const handleEditCommittee = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/committees/${committee.id}/`, committeeForm);
      toast.success('Committee updated');
      setEditingCommittee(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to update committee');
    }
  };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Committees
      </button>

      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{committee.name}</h2>
            {committee.description && <p className="text-sm text-gray-500 mt-1">{committee.description}</p>}
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${committee.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
              {committee.is_active ? 'Active' : 'Inactive'}
            </span>
            {canEdit(PERMISSIONS.EDIT_COMMITTEE) && (
              <button onClick={() => setEditingCommittee(!editingCommittee)}
                className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-semibold transition">
                {editingCommittee ? 'Cancel' : 'Edit'}
              </button>
            )}
          </div>
        </div>
        {editingCommittee && (
          <form onSubmit={handleEditCommittee} className="mt-4 pt-4 border-t space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormInput label="Name *" required value={committeeForm.name} onChange={e => setCommitteeForm({ ...committeeForm, name: e.target.value })} />
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={committeeForm.is_active} onChange={e => setCommitteeForm({ ...committeeForm, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                  <span className="text-sm font-medium text-gray-600">Active</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
              <textarea rows={2} value={committeeForm.description} onChange={e => setCommitteeForm({ ...committeeForm, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">Update</button>
              <button type="button" onClick={() => setEditingCommittee(false)} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
            </div>
          </form>
        )}
      </div>

      {/* Add Member */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Members ({members.length})</h3>
        {canAdd(PERMISSIONS.ADD_COMMITTEE) && (
          <button onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add Member
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          <h4 className="text-sm font-semibold text-gray-700">Add Member to Committee</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search Member</label>
              <input type="text" placeholder="Type name or membership number..." value={memberSearch}
                onChange={e => { setMemberSearch(e.target.value); setSelectedMember(null); }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              {searching && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
              {memberResults.length > 0 && !selectedMember && (
                <div className="border rounded-lg mt-1 max-h-40 overflow-y-auto">
                  {memberResults.map(m => (
                    <button key={m.id} type="button" onClick={() => { setSelectedMember(m); setMemberSearch(m.full_name || `${m.first_name} ${m.last_name}`); setMemberResults([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b last:border-0">
                      <span className="font-medium">{m.full_name || `${m.first_name} ${m.last_name}`}</span>
                      <span className="text-gray-400 ml-2">{m.membership_number}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedMember && (
                <p className="text-xs text-green-600 mt-1">Selected: {selectedMember.full_name || `${selectedMember.first_name} ${selectedMember.last_name}`} ({selectedMember.membership_number})</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
              <select value={role} onChange={e => setRole(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={handleAddMember} disabled={!selectedMember}
              className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition disabled:opacity-50">
              Add to Committee
            </button>
            <button type="button" onClick={() => { setShowAddForm(false); setSelectedMember(null); setMemberSearch(''); }}
              className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
          </div>
        </div>
      )}

      {/* Members Table */}
      {members.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <p className="text-gray-400">No members in this committee yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">S/N</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Membership #</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {members.map((m, idx) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{m.member_name}</td>
                  <td className="px-4 py-3 text-gray-600">{m.membership_number || '—'}</td>
                  <td className="px-4 py-3">
                    <select value={m.role} onChange={e => handleUpdateRole(m.id, e.target.value)}
                      className={`text-xs px-2 py-0.5 rounded-full font-semibold border-0 cursor-pointer ${ROLE_COLORS[m.role] || 'bg-gray-100 text-gray-600'}`}>
                      {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{m.joined_date}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canEdit(PERMISSIONS.EDIT_COMMITTEE) && (
                        <button onClick={() => { setEditingMember(m); setEditRole(m.role); }} className="text-blue-600 hover:underline text-xs">Edit</button>
                      )}
                      {canDelete(PERMISSIONS.DELETE_COMMITTEE) && (
                        <button onClick={() => handleRemoveMember(m.id)} className="text-red-500 hover:underline text-xs">Remove</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Member Role Modal */}
      {editingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditingMember(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">Edit Member</h3>
              <button onClick={() => setEditingMember(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="text-sm text-gray-600"><span className="font-medium">{editingMember.member_name}</span> — {editingMember.membership_number || 'N/A'}</p>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
              <select value={editRole} onChange={e => setEditRole(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={async () => { await handleUpdateRole(editingMember.id, editRole); setEditingMember(null); }}
                className="px-6 py-2 bg-[#0B5D1E] text-white rounded-lg text-sm font-semibold hover:bg-[#094a18] transition">Save</button>
              <button onClick={() => setEditingMember(null)}
                className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
